import {
  createUserWithCustomToken,
  firebaseAuth,
  refreshFirebaseToken,
  setToken,
} from "$utils/firebase/client";
import { derived, writable } from "svelte/store";
import { onAuthStateChanged, signOut, type User as FBUser } from "firebase/auth";
import api, { apiClientConfig } from "$utils/api";
import type {
  OutverseApi,
  PrivateWriteUser,
  User,
  CustomToken,
} from "@outverse-video/outverse-api-client-lib";
import { loginUrl } from "$utils/urls";
import * as Sentry from "@sentry/sveltekit";

export interface Session {
  loading: boolean;
  user: PrivateWriteUser | null;
  isAuthed: boolean;
  signInProvider: string | null;
  isAnonymous: boolean;
  inited: boolean;
  firebaseUser: FBUser | null;
  insideAuthFlow: boolean;
  utm: {
    source: string;
    medium: string;
    campaign: string;
    in_session: boolean;
  };
  getAPI: Promise<OutverseApi>;
  unsubscribe: () => void;
}

let resolver: (value: OutverseApi | PromiseLike<OutverseApi>) => void;

/**
 * We need to guard access to the api client until the auth has been initialised.
 * At the time of writing, this is not being done in all cases - it would be simpler if it was. In general, this
 * really only affects api calls that occur during load functions on the frontend server, because there it is possible
 * that auth has not yet been yet up.
 */
export const apiReady = new Promise<OutverseApi>((resolve) => {
  resolver = resolve;
});

const sessionStore = () => {
  const { subscribe, set, update } = writable<Session>({
    loading: true,
    user: null,
    isAuthed: false,
    signInProvider: null,
    isAnonymous: false,
    inited: false,
    firebaseUser: null,
    insideAuthFlow: false,
    utm: {
      source: "",
      medium: "",
      campaign: "",
      in_session: false,
    },
    getAPI: apiReady,
    unsubscribe: () => {},
  });

  return {
    init: () => {
      return new Promise<void>((resolve, reject) => {
        // listen to changes from firebase
        const unsub = onAuthStateChanged(firebaseAuth, async (user) => {
          // update firebase user
          update((state) => ({ ...state, firebaseUser: user }));
          if (user) {
            const idTokenResult = await user.getIdTokenResult();
            update((state) => ({
              ...state,
              loading: true,
              inited: false,
              isAuthed: true,
              isAnonymous: false,
              signInProvider: idTokenResult.signInProvider,
            }));
            // user has been set/changed, get DB user from backend, then setUser with it.
            refreshFirebaseToken(user, apiClientConfig)
              .then(() => {
                // token refreshed and set in cookies
                update((state) => ({ ...state, inited: true }));
                resolve();
                resolver(api);
                return api.apiUsersMe();
              })
              .then((res) => {
                update((state) => ({
                  ...state,
                  loading: false,
                  user: res.data,
                }));
              })
              .catch(() => {
                update((state) => ({ ...state, loading: false }));
                reject();
                resolver(api);
              });
          } else {
            // user is not authed/signed out, reset state and
            // clear token for good measure
            setToken("")
              // we don't care about failures here
              .catch(() => console.error("failed to clear token"))
              .finally(() => {
                update((state) => ({
                  ...state,
                  loading: false,
                  inited: true,
                  user: null,
                  isAuthed: false,
                  isAnonymous: true,
                }));
                resolve();
                resolver(api);
              });
          }
        });
        update((state) => ({ ...state, unsubscribe: unsub }));
      });
    },
    /** Track UTM parameters from URL.
     * This function should only be called once on page load.
     */
    trackUTM: (url: URL) => {
      const utm = {
        source: url.searchParams.get("utm_source") || "",
        medium: url.searchParams.get("utm_medium") || "",
        campaign: url.searchParams.get("utm_campaign") || "",
      };
      update((state) => ({
        ...state,
        utm: {
          in_session: !!utm.source || !!utm.medium || !!utm.campaign,
          ...utm,
        },
      }));
    },
    updateUser: (user: Partial<User>) => {
      return api.apiUsersMePartialUpdate({ patchedPrivateWriteUser: user }).then((res) => {
        update((state) => ({
          ...state,
          user: res.data,
        }));
      });
    },
    refreshUser: () => {
      return api.apiUsersMe().then((res) => {
        update((state) => ({
          ...state,
          user: res.data,
        }));
      });
    },
    refreshFirebaseUser: async () => {
      update((state) => {
        if (state.firebaseUser) state.firebaseUser.reload();
        return state;
      });
    },
    signout: (noRedirect?: boolean) => {
      return signOut(firebaseAuth).then(() => {
        setToken("");
        update((state) => ({ ...state, user: null, firebaseUser: null }));
        if (noRedirect) {
          return;
        }

        window.location.href = loginUrl();
      });
    },
    setAuthProvider(provider: string) {
      update((state) => ({ ...state, signInProvider: provider }));
    },
    async widgetAuth(communitySlug: string, authedPayload?: string, signature?: string) {
      /*
       * 1) Hit backend api to validate payload
       * 2) If response is not an error, there will be a generated custom token, so exchange the custom token for a user
       *    token.
       */
      this.setAuthProvider("custom");
      parent?.postMessage({ type: "USER_ACTION", payload: { type: "USER_AUTH" } }, "*");

      let customToken: CustomToken;
      try {
        const response = await api.apiUsersWidgetAuth({
          metadataVerification: {
            community: communitySlug,
            metadata: authedPayload,
            signature: signature,
          },
        });
        customToken = response.data;
      } catch (e) {
        console.error(e);
        const msg = "Error verifying metadata";
        Sentry.captureException(msg);
        return false;
      }

      return await createUserWithCustomToken(customToken.token);
    },
    subscribe,
    set,
    update,
  };
};

export const session = sessionStore();
export const authReady = derived(session, ($session) => $session.inited);
export const needsEmailVerification = derived(
  session,
  ($session) => $session.isAuthed && !$session?.firebaseUser?.emailVerified
);

// alias for session for the moment
export const auth = derived(session, ($session) => $session);
export const user = derived(auth, ($auth) => $auth.user);
export const firebaseUser = derived(auth, ($auth) => $auth.firebaseUser);
export const emailNotVerified = derived(firebaseUser, ($firebaseUser) =>
  $firebaseUser ? !$firebaseUser.emailVerified : false
);
export const isUserAuthed = derived(session, ($session) => $session.isAuthed);
export const insideAuthFlow = derived(session, ($session) => $session.insideAuthFlow);
export const isAuthLoaded = derived(auth, ($auth) => !$auth.loading);

export const userHasOutverseEmail = derived(
  user,
  ($user) => $user?.email?.endsWith("@outverse.com")
);
